import { useState, useEffect } from 'react';
import axios from 'axios';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { ArrowLeft, Plus, Pencil, Trash2 } from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export default function Costing() {
  const navigate = useNavigate();
  const [costings, setCostings] = useState([]);
  const [articles, setArticles] = useState([]);
  const [open, setOpen] = useState(false);
  const [editCosting, setEditCosting] = useState(null);
  const [formData, setFormData] = useState({
    article_id: '',
    article_name: '',
    material_cost: 0,
    labor_cost: 0,
    overhead_cost: 0,
    total_cost: 0,
    selling_price: 0,
    margin: 0
  });

  useEffect(() => {
    fetchCostings();
    fetchArticles();
  }, []);

  useEffect(() => {
    const material = parseFloat(formData.material_cost) || 0;
    const labor = parseFloat(formData.labor_cost) || 0;
    const overhead = parseFloat(formData.overhead_cost) || 0;
    const total = material + labor + overhead;
    const selling = parseFloat(formData.selling_price) || 0;
    const margin = selling > 0 ? ((selling - total) / selling * 100) : 0;
    setFormData(prev => ({ ...prev, total_cost: total, margin: margin }));
  }, [formData.material_cost, formData.labor_cost, formData.overhead_cost, formData.selling_price]);

  const fetchArticles = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/master-data?entity_type=article`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setArticles(response.data);
    } catch (error) {
      console.error('Failed to fetch articles');
    }
  };

  const fetchCostings = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/costing`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setCostings(response.data);
    } catch (error) {
      toast.error('Failed to fetch costings');
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const token = localStorage.getItem('token');
      const payload = {
        ...formData,
        material_cost: parseFloat(formData.material_cost),
        labor_cost: parseFloat(formData.labor_cost),
        overhead_cost: parseFloat(formData.overhead_cost),
        total_cost: parseFloat(formData.total_cost),
        selling_price: parseFloat(formData.selling_price),
        margin: parseFloat(formData.margin)
      };
      if (editCosting) {
        payload.id = editCosting.id;
        await axios.put(`${API}/costing/${editCosting.id}`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Updated successfully');
      } else {
        await axios.post(`${API}/costing`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Created successfully');
      }
      setOpen(false);
      setEditCosting(null);
      setFormData({ article_id: '', article_name: '', material_cost: 0, labor_cost: 0, overhead_cost: 0, total_cost: 0, selling_price: 0, margin: 0 });
      fetchCostings();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Operation failed');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure?')) return;
    try {
      const token = localStorage.getItem('token');
      await axios.delete(`${API}/costing/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Deleted successfully');
      fetchCostings();
    } catch (error) {
      toast.error('Delete failed');
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100">
      <header className="bg-white shadow-md border-b mb-6">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4 flex justify-between items-center">
          <div className="flex items-center gap-4">
            <Button variant="ghost" size="icon" onClick={() => navigate(-1)} data-testid="back-to-dashboard">
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <h1 className="text-2xl font-bold" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>Costing</h1>
              <p className="text-sm text-gray-600">Calculate product costs</p>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
        <div className="flex justify-end mb-6">
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button onClick={() => { setEditCosting(null); setFormData({ article_id: '', article_name: '', material_cost: 0, labor_cost: 0, overhead_cost: 0, total_cost: 0, selling_price: 0, margin: 0 }); }} data-testid="add-costing-btn">
                <Plus className="w-4 h-4 mr-2" /> Add Costing
              </Button>
            </DialogTrigger>
            <DialogContent data-testid="costing-dialog">
              <DialogHeader>
                <DialogTitle>{editCosting ? 'Edit Costing' : 'Create Costing'}</DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label>Article</Label>
                  <select className="w-full px-3 py-2 border rounded-md" value={formData.article_id}
                    onChange={(e) => {
                      const article = articles.find(a => a.id === e.target.value);
                      setFormData({ ...formData, article_id: e.target.value, article_name: article?.name || '' });
                    }} required data-testid="article-select">
                    <option value="">Select Article</option>
                    {articles.map(a => <option key={a.id} value={a.id}>{a.name}</option>)}
                  </select>
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div className="space-y-2">
                    <Label>Material Cost ($)</Label>
                    <Input type="number" step="0.01" value={formData.material_cost} onChange={(e) => setFormData({ ...formData, material_cost: e.target.value })} required data-testid="material-cost-input" />
                  </div>
                  <div className="space-y-2">
                    <Label>Labor Cost ($)</Label>
                    <Input type="number" step="0.01" value={formData.labor_cost} onChange={(e) => setFormData({ ...formData, labor_cost: e.target.value })} required data-testid="labor-cost-input" />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label>Overhead Cost ($)</Label>
                  <Input type="number" step="0.01" value={formData.overhead_cost} onChange={(e) => setFormData({ ...formData, overhead_cost: e.target.value })} required data-testid="overhead-cost-input" />
                </div>
                <div className="space-y-2">
                  <Label>Total Cost ($)</Label>
                  <Input type="number" value={formData.total_cost} disabled data-testid="total-cost-display" />
                </div>
                <div className="space-y-2">
                  <Label>Selling Price ($)</Label>
                  <Input type="number" step="0.01" value={formData.selling_price} onChange={(e) => setFormData({ ...formData, selling_price: e.target.value })} data-testid="selling-price-input" />
                </div>
                <div className="space-y-2">
                  <Label>Margin (%)</Label>
                  <Input type="number" value={formData.margin.toFixed(2)} disabled data-testid="margin-display" />
                </div>
                <Button type="submit" className="w-full" data-testid="submit-costing">{editCosting ? 'Update' : 'Create'}</Button>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        <div className="bg-white rounded-lg shadow overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Article</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Material</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Labor</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Overhead</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Total Cost</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Selling Price</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Margin %</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {costings.map((costing) => (
                <tr key={costing.id} data-testid={`costing-row-${costing.id}`}>
                  <td className="px-6 py-4">{costing.article_name}</td>
                  <td className="px-6 py-4">${costing.material_cost.toFixed(2)}</td>
                  <td className="px-6 py-4">${costing.labor_cost.toFixed(2)}</td>
                  <td className="px-6 py-4">${costing.overhead_cost.toFixed(2)}</td>
                  <td className="px-6 py-4 font-semibold">${costing.total_cost.toFixed(2)}</td>
                  <td className="px-6 py-4">${costing.selling_price?.toFixed(2) || 'N/A'}</td>
                  <td className="px-6 py-4">
                    <span className={`px-2 py-1 text-xs rounded-full ${costing.margin > 20 ? 'bg-green-100 text-green-800' : costing.margin > 10 ? 'bg-yellow-100 text-yellow-800' : 'bg-red-100 text-red-800'}`}>
                      {costing.margin?.toFixed(2) || '0'}%
                    </span>
                  </td>
                  <td className="px-6 py-4">
                    <div className="flex gap-2">
                      <Button variant="ghost" size="icon" onClick={() => { setEditCosting(costing); setFormData(costing); setOpen(true); }} data-testid={`edit-costing-${costing.id}`}>
                        <Pencil className="w-4 h-4" />
                      </Button>
                      <Button variant="ghost" size="icon" onClick={() => handleDelete(costing.id)} data-testid={`delete-costing-${costing.id}`}>
                        <Trash2 className="w-4 h-4 text-red-500" />
                      </Button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        {costings.length === 0 && <div className="text-center py-12" data-testid="no-costings"><p className="text-gray-500">No costing records found</p></div>}
      </main>
    </div>
  );
}
